import React from 'react';
import './Services.css';

const Services: React.FC = () => {
  return (
    <div className="services-page">
      <div className="container">
        {/* Hero Section */}
        <section className="services-hero">
          <h1>Our Services</h1>
          <p className="hero-subtitle">
            Comprehensive digital solutions tailored to your needs, from concept to deployment and beyond.
          </p>
        </section>

        {/* Services Grid */}
        <section className="services-overview">
          <div className="services-grid">
            <div className="service-category">
              <div className="category-icon">📱</div>
              <h2>Mobile Development</h2>
              <p>Native iOS and Android applications that deliver exceptional user experiences</p>
              <div className="service-features">
                <div className="feature">✓ Native iOS & Android Development</div>
                <div className="feature">✓ Cross-Platform Solutions (React Native)</div>
                <div className="feature">✓ UI/UX Design & Prototyping</div>
                <div className="feature">✓ App Store Optimization</div>
                <div className="feature">✓ Performance Optimization</div>
                <div className="feature">✓ Offline Functionality</div>
              </div>
            </div>

            <div className="service-category">
              <div className="category-icon">🌐</div>
              <h2>Web Applications</h2>
              <p>Scalable, responsive web platforms built with modern technologies</p>
              <div className="service-features">
                <div className="feature">✓ React & TypeScript Development</div>
                <div className="feature">✓ Progressive Web Apps (PWA)</div>
                <div className="feature">✓ Responsive Design</div>
                <div className="feature">✓ API Development & Integration</div>
                <div className="feature">✓ Database Design & Management</div>
                <div className="feature">✓ Cloud Deployment & Hosting</div>
              </div>
            </div>

            <div className="service-category">
              <div className="category-icon">🤖</div>
              <h2>AI Integration</h2>
              <p>Intelligent features powered by cutting-edge machine learning and AI</p>
              <div className="service-features">
                <div className="feature">✓ Large Language Model Integration</div>
                <div className="feature">✓ Computer Vision Solutions</div>
                <div className="feature">✓ Natural Language Processing</div>
                <div className="feature">✓ Predictive Analytics</div>
                <div className="feature">✓ Chatbots & Virtual Assistants</div>
                <div className="feature">✓ Custom AI Model Training</div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Tiers */}
        <section className="pricing-section">
          <h2>Service Packages</h2>
          <p className="pricing-subtitle">Choose the package that best fits your project needs and budget</p>
          
          <div className="pricing-grid">
            <div className="pricing-card starter">
              <div className="pricing-header">
                <h3>Starter</h3>
                <div className="price">
                  <span className="currency">$</span>
                  <span className="amount">5K</span>
                  <span className="period">- 15K</span>
                </div>
                <p>Perfect for MVPs and simple applications</p>
              </div>
              <div className="pricing-features">
                <div className="feature">✓ Basic mobile or web app</div>
                <div className="feature">✓ 3-5 core features</div>
                <div className="feature">✓ Standard UI/UX design</div>
                <div className="feature">✓ Basic testing & deployment</div>
                <div className="feature">✓ 30 days post-launch support</div>
                <div className="feature">✓ Source code delivery</div>
              </div>
              <a href="/contact" className="pricing-cta">Get Started</a>
            </div>

            <div className="pricing-card professional featured">
              <div className="featured-badge">Most Popular</div>
              <div className="pricing-header">
                <h3>Professional</h3>
                <div className="price">
                  <span className="currency">$</span>
                  <span className="amount">15K</span>
                  <span className="period">- 50K</span>
                </div>
                <p>Comprehensive solutions for growing businesses</p>
              </div>
              <div className="pricing-features">
                <div className="feature">✓ Advanced mobile & web apps</div>
                <div className="feature">✓ 8-15 core features</div>
                <div className="feature">✓ Custom UI/UX design</div>
                <div className="feature">✓ API integrations</div>
                <div className="feature">✓ Advanced testing & QA</div>
                <div className="feature">✓ 90 days post-launch support</div>
                <div className="feature">✓ Performance optimization</div>
                <div className="feature">✓ Basic AI features</div>
              </div>
              <a href="/contact" className="pricing-cta">Get Started</a>
            </div>

            <div className="pricing-card enterprise">
              <div className="pricing-header">
                <h3>Enterprise</h3>
                <div className="price">
                  <span className="currency">$</span>
                  <span className="amount">50K</span>
                  <span className="period">+</span>
                </div>
                <p>Full-scale solutions with advanced AI capabilities</p>
              </div>
              <div className="pricing-features">
                <div className="feature">✓ Complex enterprise applications</div>
                <div className="feature">✓ Unlimited features</div>
                <div className="feature">✓ Premium custom design</div>
                <div className="feature">✓ Advanced AI integration</div>
                <div className="feature">✓ Comprehensive testing suite</div>
                <div className="feature">✓ 6 months ongoing support</div>
                <div className="feature">✓ Dedicated project manager</div>
                <div className="feature">✓ Priority support & updates</div>
              </div>
              <a href="/contact" className="pricing-cta">Contact Sales</a>
            </div>
          </div>
        </section>

        {/* Process Section */}
        <section className="process-section">
          <h2>Our Development Process</h2>
          <div className="process-timeline">
            <div className="process-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Discovery & Planning</h3>
                <p>We analyze your requirements, define project scope, and create a detailed roadmap.</p>
                <div className="step-duration">1-2 weeks</div>
              </div>
            </div>
            <div className="process-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Design & Prototyping</h3>
                <p>Creating wireframes, mockups, and interactive prototypes for user validation.</p>
                <div className="step-duration">2-3 weeks</div>
              </div>
            </div>
            <div className="process-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Development</h3>
                <p>Building your application using agile methodology with regular progress updates.</p>
                <div className="step-duration">4-12 weeks</div>
              </div>
            </div>
            <div className="process-step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Testing & Optimization</h3>
                <p>Comprehensive testing, performance optimization, and bug fixes.</p>
                <div className="step-duration">1-2 weeks</div>
              </div>
            </div>
            <div className="process-step">
              <div className="step-number">5</div>
              <div className="step-content">
                <h3>Launch & Support</h3>
                <p>Deployment to production and ongoing support to ensure smooth operation.</p>
                <div className="step-duration">Ongoing</div>
              </div>
            </div>
          </div>
        </section>

        {/* Technologies Section */}
        <section className="technologies-section">
          <h2>Technologies We Use</h2>
          <div className="tech-grid">
            <div className="tech-category">
              <h3>Frontend</h3>
              <div className="tech-list">
                <span className="tech-item">React</span>
                <span className="tech-item">TypeScript</span>
                <span className="tech-item">React Native</span>
                <span className="tech-item">Next.js</span>
                <span className="tech-item">Tailwind CSS</span>
              </div>
            </div>
            <div className="tech-category">
              <h3>Backend</h3>
              <div className="tech-list">
                <span className="tech-item">Node.js</span>
                <span className="tech-item">Python</span>
                <span className="tech-item">Express</span>
                <span className="tech-item">PostgreSQL</span>
                <span className="tech-item">MongoDB</span>
              </div>
            </div>
            <div className="tech-category">
              <h3>AI & ML</h3>
              <div className="tech-list">
                <span className="tech-item">OpenAI GPT</span>
                <span className="tech-item">Groq</span>
                <span className="tech-item">TensorFlow</span>
                <span className="tech-item">PyTorch</span>
                <span className="tech-item">Hugging Face</span>
              </div>
            </div>
            <div className="tech-category">
              <h3>Cloud & DevOps</h3>
              <div className="tech-list">
                <span className="tech-item">AWS</span>
                <span className="tech-item">Vercel</span>
                <span className="tech-item">Docker</span>
                <span className="tech-item">GitHub Actions</span>
                <span className="tech-item">Cloudflare</span>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="services-cta">
          <div className="cta-content">
            <h2>Ready to Start Your Project?</h2>
            <p>Let's discuss your requirements and find the perfect solution for your needs.</p>
            <div className="cta-buttons">
              <a href="/contact" className="cta-button primary">Start Your Project</a>
              <a href="/about" className="cta-button secondary">Learn More About Us</a>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Services; 